import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { api } from './services/api';
import GoogleAuth from './components/Auth/GoogleAuth';
import Navbar from './components/Layout/Navbar';
import BankAccountsPage from './components/BankAccounts/BankAccountsPage';
import PaymentRequestsPage from './components/PaymentRequests/PaymentRequestsPage';
import AdminUsersPage from './components/Admin/AdminUsersPage';
import ApproverPage from './components/Approver/ApproverPage';
import HistoryPage from './components/History/HistoryPage';
import MyHistoryPage from './components/MyHistory/MyHistoryPage';
import ImportPage from './components/Import/ImportPage';
import DonationsPage from './components/Donations/DonationsPage';
import PdfExportsPage from './components/PdfExports/PdfExportsPage';
import MoneyFundPage from './components/MoneyFund/MoneyFundPage';
import UnpaidChecksPage from './components/UnpaidChecks/UnpaidChecksPage';
import RegisterPage from './components/Register/RegisterPage';
import PWAInstall from './components/PWAInstall/PWAInstall';
import './App.css';

function App() {
  const { user, isSignedIn, handleSignIn, handleSignOut } = useAuth();
  const [dbUser, setDbUser] = useState(null);

  useEffect(() => {
    if (isSignedIn && user) {
      api.login(user.credential).then((u) => {
        setDbUser(u);
        // Google Sheets sync disabled for now (no longer relevant).
        // To re-enable: call api.triggerSync(user.credential) for managers (u.isApprover).
      }).catch(console.error);
    }
  }, [isSignedIn, user]);

  return (
    <BrowserRouter basename={process.env.PUBLIC_URL || '/'}>
      <PageTitle />
      <Routes>
        {/* Registration is reachable without prior sign-in (manager shares the link) */}
        <Route
          path="/register"
          element={<RegisterPage user={user} isSignedIn={isSignedIn} onSignIn={handleSignIn} />}
        />
        <Route
          path="*"
          element={
            isSignedIn
              ? <MainApp user={user} dbUser={dbUser} onSignOut={handleSignOut} />
              : <LoginScreen onSignIn={handleSignIn} isSignedIn={isSignedIn} />
          }
        />
      </Routes>
      <PWAInstall />
    </BrowserRouter>
  );
}

// Base name shown in the tab; the per-page label is appended. Never "גיזבר".
const APP_NAME = 'חסד בלוד';
const PAGE_TITLES = {
  '/requests': 'בקשה חדשה',
  '/my-history': 'הבקשות שלי',
  '/bank-accounts': 'חשבונות הבנק שלי',
  '/approve': 'אישור בקשות',
  '/history': 'כל הבקשות',
  '/pdf-exports': 'אישורים לבנק',
  '/money-fund': 'קרן כספית',
  '/unpaid-checks': 'צ׳קים שטרם נפרעו',
  '/import': 'תנועות בנק',
  '/donations': 'תרומות',
  '/admin/users': 'ניהול משתמשים',
  '/register': 'רישום',
};

function PageTitle() {
  const location = useLocation();
  useEffect(() => {
    const label = PAGE_TITLES[location.pathname];
    document.title = label ? `${label} | ${APP_NAME}` : APP_NAME;
  }, [location.pathname]);
  return null;
}

function LoginScreen({ onSignIn, isSignedIn }) {
  return (
    <div style={loginStyles.wrap}>
      <div style={loginStyles.card}>
        <img src={process.env.PUBLIC_URL + '/logo-hesed.png'} alt="חסד בלוד" style={{ height: 70, maxWidth: '80%', objectFit: 'contain', marginBottom: 28 }} />
        <GoogleAuth onSignIn={onSignIn} isSignedIn={isSignedIn} />
      </div>
    </div>
  );
}

function MainApp({ user, dbUser, onSignOut }) {
  return (
    <>
      <Navbar user={user} dbUser={dbUser} onSignOut={onSignOut} />
      <Routes>
        <Route path="/requests" element={<PaymentRequestsPage user={user} dbUser={dbUser} />} />
        <Route path="/my-history" element={<MyHistoryPage user={user} />} />
        <Route path="/bank-accounts" element={<BankAccountsPage user={user} />} />
        <Route path="/approve" element={<ApproverPage user={user} />} />
        <Route path="/history" element={<HistoryPage user={user} />} />
        <Route path="/pdf-exports" element={<PdfExportsPage user={user} />} />
        <Route path="/money-fund" element={<MoneyFundPage user={user} />} />
        <Route path="/unpaid-checks" element={<UnpaidChecksPage user={user} />} />
        <Route path="/import" element={<ImportPage user={user} />} />
        <Route path="/donations" element={<DonationsPage user={user} />} />
        <Route path="/admin/users" element={<AdminUsersPage user={user} />} />
        <Route path="*" element={dbUser ? <Navigate to="/requests" replace /> : null} />
      </Routes>
    </>
  );
}

const loginStyles = {
  wrap: {
    display: 'flex', justifyContent: 'center', alignItems: 'center',
    height: '100vh', background: 'linear-gradient(160deg, #f0fafa 0%, #fdf2f4 100%)',
    direction: 'rtl',
  },
  card: {
    textAlign: 'center', padding: '48px 40px',
    background: '#fff', borderRadius: '20px',
    boxShadow: '0 8px 40px rgba(0,0,0,0.08)',
    maxWidth: '380px', width: '90%',
  },
};

export default App;
